import classNames from 'classnames/bind';
import React from 'react';
import { delay } from '../helpers';
import { IconSingleStar } from './icons';
import { Inline } from './layout';
import { Locale } from './locales';
import styles from './textarea.css';
import { Tooltip } from './tooltip';
import { TextTiny } from './typography';

const cx = classNames.bind(styles);

type TextareaProps = {
  value: string;
  lang?: Locale;
  placeholder?: string;
  onChange?(newValue: string): void;
  allowNewlines?: boolean;
  variant?: 'light' | 'popoutLight' | 'popoutDark' | 'invisible';
  /* If the text is placed on different colored backgrounds, a transparent placeholder may be needed. */
  transparentPlaceholder?: boolean;
  isInvalid?: boolean;
  width?: 'full' | 'content';
  /**
   * Padding to add inside the clickable area of the textarea.
   * This only makes sense for variant=invisible.
   **/
  padding?: Padding;
  /* Maximum height, in rems */
  maxHeight?: number;
  /* Minimum height, in rems */
  minHeight?: number;
} & Pick<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'onKeyDown' | 'onBlur' | 'onFocus' | 'disabled' | 'maxLength'
>;

export const Textarea = React.forwardRef<
  HTMLTextAreaElement | null,
  TextareaProps
>(function _Textarea(
  {
    value,
    placeholder,
    onChange,
    onKeyDown,
    allowNewlines = false,
    variant = 'light',
    transparentPlaceholder,
    isInvalid,
    width = 'full',
    padding,
    maxHeight,
    minHeight,
    lang,
    ...passthrough
  },
  propsRef,
) {
  const ref = React.useRef<HTMLTextAreaElement>(null);
  const measurerRef = React.useRef<HTMLSpanElement>(null);

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (!allowNewlines) removeNewlines(e.target);
      onChange?.(e.target.value);
    },
    [onChange, allowNewlines],
  );
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Escape') {
        e.currentTarget.blur();
      }
      onKeyDown?.(e);
    },
    [onKeyDown],
  );

  // useLayoutEffect is used to prevent the textarea from flickering on first mount
  React.useLayoutEffect(() => {
    if (typeof propsRef === 'function') propsRef(ref.current);
    else if (propsRef) propsRef.current = ref.current;

    const measurer = measurerRef.current;
    const textarea = ref.current;
    if (!measurer || !textarea) return;
    if (width === 'content') {
      // The grid-area trick doesn't work for width, so we need to set the width in JS whenever the user types
      textarea.style.width = `${measurer.offsetWidth + 1}px`;
    }

    // Set the value of the textarea, while preserving the cursor position.
    // (Previously we used a simple react controlled component.  But that caused
    //  flaky issues where the cursor would jump to the end of the textarea.  That
    //  happens when react re-renders the textarea with the controlled value after
    //  the user has already typed further... Probably our app being slow to render.
    //  Oh gosh what have we created.)
    if (textarea.value !== value) {
      const { selectionStart, selectionEnd, selectionDirection } = textarea;
      textarea.value = value;
      textarea.setSelectionRange(
        selectionStart,
        selectionEnd,
        selectionDirection,
      );
    }
  }, [value, width, maxHeight, minHeight]);

  return (
    <span
      className={styles.autosizeContainer}
      style={{
        maxHeight: maxHeight != null ? `${maxHeight}px` : undefined,
        minHeight: minHeight != null ? `${minHeight}px` : undefined,
      }}
    >
      {(variant === 'popoutLight' || variant === 'popoutDark') && (
        <span className={cx(styles.popout, variant)} />
      )}
      <textarea
        className={cx(
          styles.textarea,
          variant === 'light' ? styles.light : styles.invisible,
          transparentPlaceholder && styles.transparentPlaceholder,
          isInvalid && styles.isInvalid,
          maxHeight != null && styles.isScrollable,
        )}
        style={{ padding: formatPadding(padding) }}
        rows={1}
        cols={1}
        ref={ref}
        placeholder={placeholder}
        onChange={handleChange}
        readOnly={!onChange}
        onKeyDown={handleKeyDown}
        // match Figma's text drag behavior
        onDragStart={(e) => e.preventDefault()}
        // use the native context menu
        onContextMenu={(e) => e.stopPropagation()}
        lang={lang}
        {...passthrough}
      />
      <span
        ref={measurerRef}
        className={cx(
          styles.text,
          width === 'content' && styles.useContentWidth,
        )}
        style={{ padding: formatPadding(padding) }}
        lang={lang}
      >
        {value || placeholder}
        {value.endsWith('\n') && ' '}
      </span>
    </span>
  );
});

type PromptTextareaProps = Pick<
  TextareaProps,
  | 'value'
  | 'lang'
  | 'placeholder'
  | 'onChange'
  | 'isInvalid'
  | 'disabled'
  | 'minHeight'
  | 'allowNewlines'
>;
export const PromptTextarea = React.forwardRef(function _PromptTextarea({
  ...passthrough
}: PromptTextareaProps) {
  return (
    <div className={styles.promptTextarea}>
      <Textarea
        {...passthrough}
        padding={{ top: 6, right: 10, bottom: 32, left: 10 }}
      />
      <div className={styles.promptBadge}>
        <Inline spacing={4} yalign="center">
          <TextTiny color="gradient">Prompt</TextTiny>
          <span className={styles.promptIconStar}>
            <IconSingleStar color="colorPurple" />
          </span>
        </Inline>
      </div>
    </div>
  );
});

export function removeNewlines(textarea: HTMLTextAreaElement) {
  const { value } = textarea;
  if (!value.includes('\n')) return;
  // We don't preserve selectionEnd.  I think every OS clears the selectionEnd
  // when the user types, so it's not worth the complexity to preserve it.
  const { selectionStart } = textarea;
  const valueBefore = value.slice(0, selectionStart);
  const valueAfter = value.slice(selectionStart);
  const newBefore = valueBefore.replaceAll('\n', '');
  const newAfter = valueAfter.replaceAll('\n', '');
  textarea.value = newBefore + newAfter;
  textarea.setSelectionRange(newBefore.length, newBefore.length);
}

type Padding = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};
function formatPadding(padding: Padding | undefined) {
  if (!padding) return undefined;
  return `${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px`;
}

export type ClickInTextareaRef = {
  focus(): void;
  getSelection(): Range | undefined;
};

type Range = { start: number; end: number };

export const ClickInTextarea = React.forwardRef<
  ClickInTextareaRef,
  Omit<TextareaProps, 'variant'> & {
    variant?: 'invisible' | 'popoutLight' | 'popoutDark';
    /** If the text is placed on different colored backgrounds, a transparent placeholder may be needed. */
    transparentPlaceholder?: boolean;
    /** When enabled, clicking the text will turn it into a textarea.  Default true. */
    enableClickIn: boolean;
    /** When forced, the text will always be a textarea (no click required). */
    forceInteractive?: boolean;
    interactiveControlled?: {
      value: boolean;
      set(value: boolean): void;
    };
    lineClampWhenNotEditing?: boolean;
    placeholderWhenEditing?: boolean;
    fakeSelection?: Range;
    truncate?: boolean;
  }
>(
  (
    {
      variant = 'popoutLight',
      transparentPlaceholder,
      value,
      placeholder,
      placeholderWhenEditing,
      enableClickIn = true,
      forceInteractive,
      interactiveControlled,
      lineClampWhenNotEditing,
      onFocus,
      onBlur,
      fakeSelection,
      lang,
      truncate,
      ...props
    },
    propsRef,
  ) => {
    const [interactiveStateUncontrolled, setIntearctiveUncontrolled] =
      React.useState(false);
    const interactiveState =
      interactiveControlled?.value ?? interactiveStateUncontrolled;
    const setIntearctive =
      interactiveControlled?.set ?? setIntearctiveUncontrolled;

    const interactive = interactiveState || forceInteractive;
    const enabledAtMouseDown = React.useRef(false);
    const focusCausedByMouse = React.useRef(0);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const spanRef = React.useRef<HTMLSpanElement>(null);

    React.useEffect(() => {
      if (!interactive) return;
      const textarea = textareaRef.current;
      if (!textarea) return;
      // don't focus if the textarea wasn't caused by a user interaction:
      if (forceInteractive) return;
      textarea.focus();
      textarea.select();
      // only focus when we start editing (hence checking interactive & NO OTHER STATE)
    }, [interactive]);

    const isOverflowing = spanRef.current
      ? spanRef.current.scrollWidth > spanRef.current.clientWidth
      : false;

    React.useImperativeHandle(propsRef, () => ({
      focus: () => {
        // the textarea ref doesn't get cleared when interactive is false, so we check:
        if (interactive && textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.select();
        } else {
          setIntearctive(true);
        }
      },
      getSelection() {
        const textarea = textareaRef.current;
        if (!textarea || !interactive) return;
        return {
          start: textarea.selectionStart,
          end: textarea.selectionEnd,
        };
      },
    }));

    return interactive ? (
      <Textarea
        ref={textareaRef}
        placeholder={placeholderWhenEditing ? placeholder : undefined}
        transparentPlaceholder={transparentPlaceholder}
        value={value}
        onFocus={(e) => {
          // If forceInteractive was used, we copy that into interactiveState
          // so that the textarea remains interactive even if forceInteractive
          // is removed.
          if (!interactiveState) setIntearctive(true);
          onFocus && onFocus(e);
        }}
        onBlur={(e) => {
          setIntearctive(false);
          onBlur && onBlur(e);
        }}
        variant={variant}
        lang={lang}
        {...props}
      />
    ) : (
      <span
        ref={spanRef}
        className={cx(
          styles.text,
          truncate && styles.truncate,
          !value && styles.isPlaceholder,
          transparentPlaceholder && styles.transparentPlaceholder,
          lineClampWhenNotEditing && styles.lineClamp,
        )}
        style={{ padding: formatPadding(props.padding) }}
        // One user of this component (sitemap_sections) changes `enableClickIn`
        // if the parent is clicked.  We need to apply the value from the
        // mouseDown at the click time.
        onMouseDown={async () => {
          enabledAtMouseDown.current = enableClickIn;
          focusCausedByMouse.current++;
          await delay(0);
          focusCausedByMouse.current--;
        }}
        onMouseUp={async () => {
          focusCausedByMouse.current++;
          await delay(0);
          focusCausedByMouse.current--;
          enabledAtMouseDown.current = false;
        }}
        onMouseLeave={() => (enabledAtMouseDown.current = false)}
        onClick={() => enabledAtMouseDown.current && setIntearctive(true)}
        onFocus={() => focusCausedByMouse.current === 0 && setIntearctive(true)}
        tabIndex={0}
        lang={lang}
      >
        <FakedSelection range={fakeSelection} text={value || placeholder} />
        {isOverflowing && truncate && (
          <Tooltip label={value} anchorRef={spanRef} />
        )}
      </span>
    );
  },
);

function FakedSelection({
  range,
  text,
}: {
  range: Range | undefined;
  text: string | undefined;
}) {
  if (!text) return <br />;
  const min = range ? Math.min(range.start, range.end) : 0;
  const max = range ? Math.max(range.start, range.end) : 0;
  // This is so broken for mixed LTR + RTL text.  Sorry:
  const before = text.slice(0, min);
  const selected = text.slice(min, max);
  const after = text.slice(max);
  return (
    <>
      {before}
      <span className={styles.fakeSelection}>{selected}</span>
      {after}
    </>
  );
}
